import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
} from 'react';
import { getActions, withGlobal } from '../../../global';

import type { AnimationLevel, ISettings } from '../../../types';
import { LeftColumnContent, MiddleColumnContent } from '../../../types';
import type { ApiChat } from '../../../api/types';
import type { TabState, GlobalState } from '../../../global/types';

import {
  selectCurrentMessageList,
  selectIsCurrentUserPremium,
  selectTabState,
  selectTheme,
} from '../../../global/selectors';
import useConnectionStatus from '../../../hooks/useConnectionStatus';
import useAppLayout from '../../../hooks/useAppLayout';

import Button from '../../ui/Button';
import ShowTransition from '../../ui/ShowTransition';
import ConnectionStatusOverlay from '../ConnectionStatusOverlay';
import StatusButton from './StatusButton';
import { useTranslation } from 'react-i18next';
import IconSvg from '../../ui/IconSvg';
import classNames from 'classnames';
import SearchInput from '../../ui/SearchInput';
import useFlag from '../../../hooks/useFlag';
import useLastCallback from '../../../hooks/useLastCallback';
import { FeedLeftList, FeedMiddleList } from '../../main/Main';
import useHistoryBack from '../../../hooks/useHistoryBack';
import { getPromptInstall } from '../../../util/installPrompt';

import './LeftMainHeader.scss';

type OwnProps = {
  shouldHideSearch?: boolean;
  contactsFilter: string;
  channelQuery?: string;
  isClosingSearch?: boolean;
  shouldSkipTransition?: boolean;
  onSearchQuery: (query: string) => void;
  onSelectArchived: () => void;
  onReset: () => void;
  feedMiddleScreen: FeedMiddleList;
  feedLeftScreen: FeedLeftList;
  setFeedMiddleScreen: (screen: FeedMiddleList) => void;
  onClickAi: () => void;
};

type StateProps = {
  searchQuery?: string;
  isLoading: boolean;
  currentUserId?: string;
  globalSearchChatId?: string;
  searchDate?: number;
  theme: ISettings['theme'];
  animationLevel: AnimationLevel;
  chatsById?: Record<string, ApiChat>;
  isMessageListOpen: boolean;
  isCurrentUserPremium?: boolean;
  isConnectionStatusMinimized: ISettings['isConnectionStatusMinimized'];
  areChatsLoaded?: boolean;
  canInstall?: boolean;
} & Pick<GlobalState, 'connectionState' | 'isSyncing' | 'archiveSettings'> &
  Pick<TabState, 'canInstall' | 'leftScreen'>;

const LeftMainHeader: FC<OwnProps & StateProps> = ({
  shouldHideSearch,
  onReset,
  searchQuery,
  channelQuery,
  isCurrentUserPremium,
  globalSearchChatId,
  connectionState,
  isSyncing,
  isMessageListOpen,
  isConnectionStatusMinimized,
  areChatsLoaded,
  contactsFilter,
  canInstall,
  leftScreen,
  isClosingSearch,
  onSearchQuery,
  feedMiddleScreen,
  feedLeftScreen,
  setFeedMiddleScreen,
  onClickAi,
}) => {
  const { setSettingOption, toggleLeftColumn } = getActions();

  const { t } = useTranslation();

  const { isMobile } = useAppLayout();

  const [isSearchOpen, openSearch, closeSearch] = useFlag();

  const { connectionStatus, connectionStatusText, connectionStatusPosition } =
    useConnectionStatus(
      t,
      connectionState,
      isSyncing,
      isMessageListOpen,
      isConnectionStatusMinimized,
      !areChatsLoaded
    );

  const { toggleScreens } = useHistoryBack();

  const handleClickQuestion = useLastCallback(() => {
    toggleScreens({
      leftScreen: LeftColumnContent.ChatList,
      middleScreen: MiddleColumnContent.QuickLinks,
    });
    toggleLeftColumn();
  });

  const toggleConnectionStatus = useCallback(() => {
    setSettingOption({
      isConnectionStatusMinimized: !isConnectionStatusMinimized,
    });
  }, [isConnectionStatusMinimized, setSettingOption]);

  const handleSearchFocus = useCallback(() => {
    if (!searchQuery) {
      onSearchQuery('');
    }
  }, [searchQuery, onSearchQuery]);

  const toggleShowSearch = useCallback(() => {
    if (isSearchOpen) {
      closeSearch();
      onReset();
    } else {
      handleSearchFocus();
      openSearch();
      setTimeout(() => {
        const searchInput =
          document.querySelector<HTMLInputElement>('#LeftMainSearch');
        searchInput?.focus();
      }, 200);
    }
  }, [isSearchOpen]);

  useEffect(() => {
    if (
      searchQuery &&
      ![
        LeftColumnContent.GlobalSearch,
        LeftColumnContent.Contacts,
        LeftColumnContent.Feed,
      ].includes(leftScreen)
    ) {
      closeSearch();
    }
  }, [leftScreen, searchQuery]);

  useLayoutEffect(() => {
    if (searchQuery || contactsFilter || channelQuery) {
      openSearch();
    }
  }, [searchQuery, contactsFilter, channelQuery]);

  useEffect(() => {
    if (leftScreen === LeftColumnContent.GlobalSearch && !isSearchOpen) {
      toggleShowSearch();
    }
  }, [leftScreen]);

  return (
    <>
      {isMobile &&
        leftScreen === LeftColumnContent.Feed &&
        feedMiddleScreen === FeedMiddleList.Settings && (
          <Button
            round
            color='translucent'
            onClick={() => setFeedMiddleScreen(FeedMiddleList.Main)}
            ariaLabel='Back'
          >
            <div className='icon-svg'>
              <IconSvg name='arrow-left' />
            </div>
          </Button>
        )}
      <h4
        className={classNames({
          'd-none': isSearchOpen,
          //'is-feed': leftScreen === LeftColumnContent.Feed,
        })}
      >
        {leftScreen !== LeftColumnContent.GlobalSearch &&
          t(`Content.${leftScreen}`)}
      </h4>

      {/*
        {selectedSearchDate && (
          <PickerSelectedItem
            icon='calendar'
            title={selectedSearchDate}
            canClose
            isMinimized={Boolean(globalSearchChatId)}
            className='search-date'
            onClick={setGlobalSearchDate}
            clickArg={clearedDateSearchParam}
          />
        )}
        {globalSearchChatId && (
          <PickerSelectedItem
            chatOrUserId={globalSearchChatId}
            onClick={setGlobalSearchChatId}
            canClose
            clickArg={clearedChatSearchParam}
          />
        )}
      */}
      <div className='btn-block'>
        {![FeedLeftList.Explore, FeedLeftList.Following].includes(
          leftScreen === LeftColumnContent.Feed ? feedLeftScreen : 0
        ) && (
          <>
            {!isSearchOpen && canInstall && !isMobile && (
              <Button isLink onClick={getPromptInstall()}>
                <IconSvg name='download' w='14' h='14' />
                Install App
              </Button>
            )}
            <Button
              round
              color='translucent'
              size='tiny'
              onClick={toggleShowSearch}
              ariaLabel={String(t(isSearchOpen ? 'Close' : 'Search'))}
            >
              <div className='icon-svg'>
                <IconSvg name={isSearchOpen ? 'arrow-left' : 'search-ql'} />
              </div>
            </Button>
            <div
              className={classNames('opacity-transition width-in shown', {
                open: isSearchOpen,
              })}
            >
              <SearchInput
                value={
                  isClosingSearch
                    ? undefined
                    : searchQuery || contactsFilter || channelQuery
                }
                inputId='LeftMainSearch'
                onChange={onSearchQuery}
              />
            </div>
          </>
        )}

        {/* {leftScreen === LeftColumnContent.ChatList && (
          <Button
            round
            color='translucent'
            className='activated'
            size='tiny'
            onClick={handleClickQuestion}
          >
            <i className='icon icon-svg'>
              <IconSvg name='quick-links' /> 
            </i>
          </Button>
        )} */}

        {(leftScreen === LeftColumnContent.ChatList ||
          leftScreen === LeftColumnContent.GlobalSearch) && (
          <Button round size='tiny' color='translucent' onClick={onClickAi}>
            <i className='icon icon-svg'>
              <IconSvg name='ai-bot-ql' />
            </i>
          </Button>
        )}
        {isMobile &&
          leftScreen === LeftColumnContent.Feed &&
          feedMiddleScreen !== FeedMiddleList.Settings && (
            <Button
              round
              color='translucent'
              ariaLabel='Settimgs'
              size='tiny'
              onClick={() => setFeedMiddleScreen(FeedMiddleList.Settings)}
            >
              <i className='icon-svg'>
                <IconSvg name='settings' />
              </i>
            </Button>
          )}
      </div>

      <ShowTransition
        isOpen={connectionStatusPosition === 'overlay'}
        isCustom
        className='connection-state-wrapper'
      >
        <ConnectionStatusOverlay
          connectionStatus={connectionStatus}
          connectionStatusText={connectionStatusText!}
          onClick={toggleConnectionStatus}
        />
      </ShowTransition>
    </>
  );
};

export default memo(
  withGlobal<OwnProps>((global): StateProps => {
    const tabState = selectTabState(global);
    const {
      query: searchQuery,
      fetchingStatus,
      chatId,
      date,
      filter,
    } = tabState.globalSearch;
    const { currentUserId, connectionState, isSyncing, archiveSettings } =
      global;
    const { byId: chatsById } = global.chats;
    const { isConnectionStatusMinimized, animationLevel } =
      global.settings.byKey;

    return {
      searchQuery,
      isLoading: fetchingStatus
        ? Boolean(fetchingStatus.chats || fetchingStatus.messages)
        : false,
      currentUserId,
      chatsById,
      globalSearchChatId: chatId,
      searchDate: date,
      theme: selectTheme(global),
      animationLevel,
      connectionState,
      isSyncing,
      isMessageListOpen: Boolean(selectCurrentMessageList(global)),
      isConnectionStatusMinimized,
      isCurrentUserPremium: selectIsCurrentUserPremium(global),
      areChatsLoaded: Boolean(global.chats.listIds.active),
      canInstall: Boolean(tabState.canInstall),
      archiveSettings,
      leftScreen: tabState.leftScreen,
    };
  })(LeftMainHeader)
);
